import { defaultPrinterSettings, electronPrintSettingsSlice } from '@store/slices/electronPrintSettings';
import { useAppDispatch, useAppSelector } from '..';
import { ElectronPrintSettings } from '@interfaces/general';

export const useElectronPrintSettingsFromStore = () => {
  return useAppSelector((state) => state.electronPrintSettings);
};

// Impresora "principal" para acciones de impresión MANUAL (botones de imprimir
// pedido) y para flags de auto-print de pantalla. Reemplaza el acceso fijo a
// `.Printer1`, que se rompía con el modelo flexible al quitar esa impresora.
// Preferencia: primera habilitada con impresora asignada → primera habilitada →
// primera de la lista. Nunca undefined (el store garantiza ≥1; default defensivo).
export const useMainPrinter = (): ElectronPrintSettings => {
  const all = useElectronPrintSettingsFromStore();
  const keys = Object.keys(all);
  if (!keys.length) return defaultPrinterSettings();
  const withPrinter = keys.find((k) => all[k].electronEnabled !== false && all[k].electronPrinterName);
  const enabled = keys.find((k) => all[k].electronEnabled !== false);
  return all[withPrinter || enabled || keys[0]];
};

// Selección PURA de impresoras a usar al REIMPRIMIR: la principal SIEMPRE (preserva el
// comportamiento legacy) + cualquier otra con el opt-in `electronReprintEnabled`,
// deduplicada por key. Si nadie activa el flag, devuelve solo [main] → idéntico al
// comportamiento actual. Pura (sin hooks) para poder testearla sin renderHook/Provider.
export const selectReprintPrinters = (
  all: Record<string, ElectronPrintSettings>,
  main: ElectronPrintSettings,
): ElectronPrintSettings[] => {
  const keys = Object.keys(all);
  const mainKey = keys.find((k) => all[k] === main);
  const extras = keys
    .filter((k) => k !== mainKey)
    .map((k) => all[k])
    .filter((s) => s.electronEnabled !== false && s.electronPrinterName && s.electronReprintEnabled === true);
  return [main, ...extras];
};

// Impresoras a usar al reimprimir un ticket (botones "Reimprimir" de Orders). Wrapper
// delgado sobre selectReprintPrinters.
export const useReprintPrinters = (): ElectronPrintSettings[] => {
  const all = useElectronPrintSettingsFromStore();
  const main = useMainPrinter();
  return selectReprintPrinters(all, main);
};

export const useElectronPrintSettingsActions = () => {
  const dispatch = useAppDispatch();

  // printerKey es un id dinámico (modelo flexible). V1 sigue pasando 'Printer1'…
  // que son strings → compatible.
  const updateElectronPrintSettings = (settings: Partial<ElectronPrintSettings>, printerKey: string) =>
    dispatch(electronPrintSettingsSlice.actions.updateElectronPrintSettings({ settings, printerKey }));

  const addPrinter = (patch?: Partial<ElectronPrintSettings>) =>
    dispatch(electronPrintSettingsSlice.actions.addPrinter(patch ? { patch } : undefined));

  const removePrinter = (printerKey: string) =>
    dispatch(electronPrintSettingsSlice.actions.removePrinter({ printerKey }));

  const resetPrinters = () => dispatch(electronPrintSettingsSlice.actions.reset());

  return { updateElectronPrintSettings, addPrinter, removePrinter, resetPrinters };
};
